import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["thumbnail", "loading", "error"]
  static values = {
    originalUrl: String,
    filename: String
  }

  connect() {
    this.setupImageLoading()
    this.setupVideoLoading()
  }

  // Setup image loading with loading states
  setupImageLoading() {
    this.thumbnailTargets.forEach(img => {
      // Add loading class initially
      img.classList.add('loading')

      // Set up load event listener
      img.addEventListener('load', () => {
        this.handleImageLoad(img)
      })

      // Set up error event listener
      img.addEventListener('error', (event) => {
        this.handleImageError(event, img)
      })

      // Set timeout for slow loading images
      setTimeout(() => {
        if (img.classList.contains('loading')) {
          this.handleImageTimeout(img)
        }
      }, 10000) // 10 seconds timeout
    })
  }

  // Setup video loading
  setupVideoLoading() {
    const videos = this.element.querySelectorAll('video')
    videos.forEach(video => {
      video.addEventListener('loadstart', () => {
        this.showVideoLoading(video)
      })

      video.addEventListener('canplay', () => {
        this.hideVideoLoading(video)
      })

      video.addEventListener('error', () => {
        this.handleVideoError(video)
      })
    })
  }

  // Handle successful image load
  handleImageLoad(img) {
    img.classList.remove('loading')
    img.classList.add('attachment-loaded')

    // Add subtle animation
    img.style.opacity = '0'
    setTimeout(() => {
      img.style.opacity = '1'
    }, 100)
  }

  // Handle image loading error
  handleImageError(event, img) {
    img.classList.remove('loading')
    img.classList.add('error')

    console.warn(`Failed to load image: ${this.filenameValue}`)
    this.showFallback(img)
  }

  // Handle image loading timeout
  handleImageTimeout(img) {
    img.classList.remove('loading')
    img.classList.add('error')

    console.warn(`Image loading timeout: ${this.filenameValue}`)
    this.showFallback(img)
  }

  // Show fallback for failed images
  showFallback(img) {
    const container = img.parentElement
    const originalUrl = this.originalUrlValue || img.dataset.originalUrl || img.src
    const filename = this.filenameValue || 'Unknown file'

    container.innerHTML = `
      <div class="attachment-error-fallback">
        <i class="fas fa-exclamation-triangle fa-2x mb-2"></i>
        <div class="filename">${filename}</div>
        <div class="error-message">Unable to load thumbnail</div>
        <a href="${originalUrl}" target="_blank" class="btn btn-sm btn-outline-primary">
          <i class="fas fa-external-link-alt me-1"></i>View Original
        </a>
      </div>
    `
  }

  // Show video loading indicator
  showVideoLoading(video) {
    const container = video.parentElement
    const existingLoader = container.querySelector('.video-loading')

    if (!existingLoader) {
      const loader = document.createElement('div')
      loader.className = 'video-loading'
      loader.innerHTML = `
        <div class="text-center">
          <i class="fas fa-spinner fa-spin fa-2x text-muted mb-2"></i>
          <p class="text-muted small">Loading video...</p>
        </div>
      `
      loader.style.cssText = 'position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);'
      container.style.position = 'relative'
      container.appendChild(loader)
    }
  }

  // Hide video loading indicator
  hideVideoLoading(video) {
    const container = video.parentElement
    const loader = container.querySelector('.video-loading')

    if (loader) {
      loader.remove()
    }
  }

  // Handle video loading error
  handleVideoError(video) {
    const container = video.parentElement
    this.hideVideoLoading(video)

    container.innerHTML = `
      <div class="attachment-error-fallback">
        <i class="fas fa-video-slash fa-2x mb-2"></i>
        <div class="filename">${video.title || 'Video'}</div>
        <div class="error-message">Unable to load video</div>
        <a href="${video.src}" target="_blank" class="btn btn-sm btn-outline-primary">
          <i class="fas fa-download me-1"></i>Download
        </a>
      </div>
    `
  }

  // Retry loading failed attachment
  retry(event) {
    event.preventDefault()
    const img = this.element.querySelector('.attachment-thumbnail.error')

    if (img) {
      img.classList.remove('error')
      img.classList.add('loading')
      img.src = img.dataset.originalUrl || this.originalUrlValue
    }
  }

  // Handle attachment download
  download(event) {
    event.preventDefault()
    const url = event.currentTarget.href

    // Create a temporary link to trigger download
    const link = document.createElement('a')
    link.href = url
    link.download = this.filenameValue || 'attachment'
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Show attachment details modal (if implemented)
  showDetails(event) {
    event.preventDefault()
    // This could be expanded to show a modal with attachment details
    console.log('Show attachment details:', this.filenameValue)
  }

  // Handle drag and drop for uploads (future enhancement)
  handleDragOver(event) {
    event.preventDefault()
    event.currentTarget.classList.add('drag-over')
  }

  handleDragLeave(event) {
    event.preventDefault()
    event.currentTarget.classList.remove('drag-over')
  }

  handleDrop(event) {
    event.preventDefault()
    event.currentTarget.classList.remove('drag-over')

    const files = event.dataTransfer.files
    if (files.length > 0) {
      console.log('Files dropped:', files)
      // Handle file uploads here
    }
  }
}